import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionBankService } from '../services/questionBankService';
import { QuestionRequest, QuestionType, QuestionOptionRequest } from '../types';
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from '../components/Icons';
import ImageUpload from '../components/ImageUpload';
import RichTextEditor from '../components/RichTextEditor';
import MatrixEditor from '../components/MatrixEditor';

const QUESTION_TYPES = [
  { value: 'SINGLE_CHOICE' as QuestionType, label: '单选题', icon: '◉' },
  { value: 'MULTIPLE_CHOICE' as QuestionType, label: '多选题', icon: '☑' },
  { value: 'TEXT' as QuestionType, label: '文本输入', icon: '⌨' },
  { value: 'SLIDER' as QuestionType, label: '滑块', icon: '─' },
  { value: 'MATRIX' as QuestionType, label: '矩阵题', icon: '▦' },
  { value: 'DESCRIPTION' as QuestionType, label: '说明文本', icon: '📄' },
  { value: 'FILL_IN_BLANK' as QuestionType, label: '填空题', icon: '___' },
  { value: 'DYNAMIC_FILL_IN_BLANK' as QuestionType, label: '动态填空', icon: '＋___' },
  { value: 'DATE' as QuestionType, label: '日期选择', icon: '📅' },
  { value: 'SHORT_TEXT' as QuestionType, label: '短文本', icon: '✏' },
  { value: 'DROPDOWN' as QuestionType, label: '下拉选择', icon: '▼' },
];

const BankQuestionEditPage: React.FC = () => {
  const { bankId, questionId } = useParams<{ bankId: string; questionId: string }>();
  const navigate = useNavigate();
  const isNew = questionId === 'new';

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<QuestionRequest>({
    title: '',
    type: 'SINGLE_CHOICE',
    imageUrl: '',
    required: false,
    orderNum: 0,
    questionNumber: '',
    settings: '',
    options: [],
  });

  useEffect(() => {
    if (!isNew && questionId && bankId) {
      fetchQuestion();
    } else {
      setFormData(prev => ({
        ...prev,
        options: [
          { content: '选项 1', imageUrl: '', score: 0, orderNum: 0, isOtherOption: false },
          { content: '选项 2', imageUrl: '', score: 0, orderNum: 1, isOtherOption: false },
        ],
      }));
    }
  }, [questionId, bankId, isNew]);

  const fetchQuestion = async () => {
    try {
      setLoading(true);
      const question = await questionBankService.getBankQuestion(Number(bankId), Number(questionId));
      setFormData({
        title: question.title,
        type: question.type,
        imageUrl: question.imageUrl || '',
        required: question.required,
        orderNum: question.orderNum,
        questionNumber: question.questionNumber || '',
        settings: question.settings || '',
        options: question.options.map(opt => ({
          content: opt.content,
          imageUrl: opt.imageUrl || '',
          score: opt.score || 0,
          orderNum: opt.orderNum,
          isOtherOption: opt.isOtherOption,
        })),
      });
    } catch (error) {
      console.error('Failed to fetch question:', error);
      alert('获取题目失败');
    } finally {
      setLoading(false);
    }
  };

  const validateMatrixSettings = (settings: string): string | null => {
    if (!settings || !settings.trim()) return '请填写矩阵题设置';
    try {
      const config = JSON.parse(settings);
      if (!Array.isArray(config.rows) || config.rows.length === 0) return '矩阵题必须包含至少一个行（rows）';
      if (!Array.isArray(config.columns) || config.columns.length === 0) return '矩阵题必须包含至少一个列（columns）';
      if (config.rows.some((r: any) => typeof r !== 'string' || !r.trim())) return '行标题不能为空';
      if (config.columns.some((c: any) => typeof c !== 'string' || !c.trim())) return '列标题不能为空';
      return null;
    } catch (e) {
      return `JSON 格式错误：${(e as Error).message}`;
    }
  };

  const validateSliderSettings = (settings: string): string | null => {
    if (!settings || !settings.trim()) return '请填写滑块题设置';
    try {
      const config = JSON.parse(settings);
      if (typeof config.min !== 'number') return '滑块题必须设置 min（最小值）';
      if (typeof config.max !== 'number') return '滑块题必须设置 max（最大值）';
      if (config.min >= config.max) return 'min 必须小于 max';
      if (config.step !== undefined && (typeof config.step !== 'number' || config.step <= 0)) return 'step 必须为正数';
      return null;
    } catch (e) {
      return `JSON 格式错误：${(e as Error).message}`;
    }
  };

  const handleSubmit = async () => {
    if (formData.type !== 'DESCRIPTION' && !formData.title.trim()) {
      alert('请输入题目标题');
      return;
    }

    if (needsOptions() && formData.options.length === 0) {
      alert('请至少添加一个选项');
      return;
    }

    if (formData.type === 'MATRIX') {
      const error = validateMatrixSettings(formData.settings || '');
      if (error) { alert(error); return; }
    }

    if (formData.type === 'SLIDER') {
      const error = validateSliderSettings(formData.settings || '');
      if (error) { alert(error); return; }
    }

    try {
      setLoading(true);
      const submitData = { ...formData };
      if (submitData.type === 'DESCRIPTION' && !submitData.title.trim()) {
        submitData.title = '说明文本';
      }

      if (isNew) {
        await questionBankService.createBankQuestion(Number(bankId), submitData);
      } else {
        await questionBankService.updateBankQuestion(Number(bankId), Number(questionId), submitData);
      }

      navigate(`/question-banks/${bankId}`);
    } catch (error) {
      console.error('Failed to save question:', error);
      alert('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const needsOptions = () => {
    return formData.type === 'SINGLE_CHOICE' || formData.type === 'MULTIPLE_CHOICE' || formData.type === 'DROPDOWN';
  };

  const isDescriptionType = () => {
    return formData.type === 'DESCRIPTION';
  };

  const addOption = () => {
    const newOption: QuestionOptionRequest = {
      content: `选项 ${formData.options.length + 1}`,
      imageUrl: '',
      score: 0,
      orderNum: formData.options.length,
      isOtherOption: false,
    };
    setFormData({ ...formData, options: [...formData.options, newOption] });
  };

  const removeOption = (index: number) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    newOptions.forEach((opt, i) => { opt.orderNum = i; });
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (index: number, field: keyof QuestionOptionRequest, value: any) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const moveOption = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === formData.options.length - 1) return;
    const newOptions = [...formData.options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
    newOptions.forEach((opt, i) => { opt.orderNum = i; });
    setFormData({ ...formData, options: newOptions });
  };

  if (loading && !isNew) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/question-banks/${bankId}`)}
          className="flex items-center text-on-surface-variant hover:text-on-surface mb-4"
        >
          <ArrowLeftIcon size={20} className="mr-2" />
          返回题库
        </button>
        <h1 className="text-2xl font-bold font-headline text-on-surface">
          {isNew ? '添加题目' : '编辑题目'}
        </h1>
      </div>

      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6">
        {/* 题目标题 */}
        {formData.type !== 'DESCRIPTION' && (
          <div className="mb-6">
            <label className="block text-sm font-medium font-label text-on-surface mb-2">题目标题 *</label>
            <input
              type="text"
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="请输入题目标题"
            />
          </div>
        )}

        {/* 题目类型 */}
        <div className="mb-6">
          <label className="block text-sm font-medium font-label text-on-surface mb-2">题目类型 *</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {QUESTION_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  const newFormData = { ...formData, type: type.value };
                  if (type.value !== 'SINGLE_CHOICE' && type.value !== 'MULTIPLE_CHOICE' && type.value !== 'DROPDOWN') {
                    newFormData.options = [];
                  }
                  if (type.value === 'DESCRIPTION') newFormData.required = false;
                  if (type.value === 'FILL_IN_BLANK' || type.value === 'DYNAMIC_FILL_IN_BLANK') newFormData.required = false;

                  if (!formData.settings || formData.type !== type.value) {
                    if (type.value === 'DESCRIPTION') {
                      newFormData.settings = '<p>请在此输入说明内容</p>';
                    } else if (type.value === 'MATRIX') {
                      newFormData.settings = JSON.stringify({ rows: ["行1", "行2"], columns: ["列1", "列2", "列3"] }, null, 2);
                    } else if (type.value === 'SLIDER') {
                      newFormData.settings = JSON.stringify({ min: 0, max: 100, step: 1, defaultValue: 50 }, null, 2);
                    } else if (type.value === 'FILL_IN_BLANK') {
                      newFormData.settings = JSON.stringify({ fields: [{ key: "field_1", label: "", suffix: "", placeholder: "请输入" }] }, null, 2);
                    } else if (type.value === 'DYNAMIC_FILL_IN_BLANK') {
                      newFormData.settings = JSON.stringify({ fields: [{ key: "field_1", label: "", suffix: "", placeholder: "请输入" }], minGroups: 1, maxGroups: 5 }, null, 2);
                    } else if (type.value === 'TEXT') {
                      newFormData.settings = JSON.stringify({ maxLength: 500, minLength: 10, placeholder: "请输入您的答案" }, null, 2);
                    } else if (type.value === 'DATE') {
                      newFormData.settings = JSON.stringify({ placeholder: "请选择日期" }, null, 2);
                    } else if (type.value === 'SHORT_TEXT') {
                      newFormData.settings = JSON.stringify({ maxLength: 100, placeholder: "请输入" }, null, 2);
                    } else {
                      newFormData.settings = '';
                    }
                  }
                  setFormData(newFormData);
                }}
                className={`p-3 rounded-xl text-center transition-all ${
                  formData.type === type.value
                    ? 'border-2 border-primary-500 bg-primary-50 text-primary-700'
                    : 'bg-surface-container-high hover:bg-surface-container-low'
                }`}
              >
                <div className="text-2xl mb-1">{type.icon}</div>
                <div className="text-xs font-medium font-label">{type.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* 题号 */}
        <div className="mb-6">
          <label className="block text-sm font-medium font-label text-on-surface mb-2">题号（选填）</label>
          <input
            type="text"
            className="w-32 px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
            value={formData.questionNumber || ''}
            onChange={(e) => setFormData({ ...formData, questionNumber: e.target.value })}
            placeholder="如：1、1.1、一、A"
          />
        </div>

        {/* 图片 */}
        <ImageUpload
          label="题目图片（可选）"
          value={formData.imageUrl}
          onChange={(url) => setFormData({ ...formData, imageUrl: url })}
          className="mb-6"
        />

        {/* 是否必填 */}
        {!isDescriptionType() && (
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
              />
              <span className="ml-2 text-sm font-medium font-label text-on-surface">必填题目</span>
            </label>
          </div>
        )}

        {/* 选项列表 */}
        {needsOptions() && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium font-label text-on-surface">选项列表 *</label>
              <button type="button" onClick={addOption} className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                <PlusIcon size={16} />
                添加选项
              </button>
            </div>
            <div className="space-y-3">
              {formData.options.map((option, index) => (
                <div key={index} className="shadow-soft rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-surface-container-high rounded-full flex items-center justify-center text-sm font-medium text-on-surface-variant">
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div className="flex-1 space-y-3">
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                        value={option.content}
                        onChange={(e) => updateOption(index, 'content', e.target.value)}
                        placeholder="选项内容"
                      />
                      <div className="space-y-3">
                        <ImageUpload
                          label="选项图片（可选）"
                          value={option.imageUrl || ''}
                          onChange={(url) => updateOption(index, 'imageUrl', url)}
                        />
                        <div>
                          <label className="block text-xs text-on-surface-variant mb-1 font-label">分数（可选）</label>
                          <input
                            type="number"
                            className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                            value={option.score || 0}
                            onChange={(e) => updateOption(index, 'score', parseInt(e.target.value) || 0)}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <label className="flex items-center text-xs">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={option.isOtherOption}
                          onChange={(e) => updateOption(index, 'isOtherOption', e.target.checked)}
                        />
                        <span className="ml-1 text-on-surface font-medium font-label">允许用户输入其他内容</span>
                      </label>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button type="button" onClick={() => moveOption(index, 'up')} disabled={index === 0} className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed" title="上移">
                        <ChevronUpIcon size={20} />
                      </button>
                      <button type="button" onClick={() => moveOption(index, 'down')} disabled={index === formData.options.length - 1} className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed" title="下移">
                        <ChevronDownIcon size={20} />
                      </button>
                      <button type="button" onClick={() => removeOption(index)} className="p-1 text-danger-600 hover:text-danger-700" title="删除">
                        <TrashIcon size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 文本输入设置 */}
        {formData.type === 'TEXT' && (
          <div className="mb-6">
            <label className="block text-sm font-medium font-label text-on-surface mb-2">高级设置（JSON 格式，可选）</label>
            <textarea
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 font-mono text-sm"
              rows={4}
              value={formData.settings}
              onChange={(e) => setFormData({ ...formData, settings: e.target.value })}
              placeholder='{"maxLength": 500, "minLength": 10}'
            />
          </div>
        )}

        {/* 日期题设置 */}
        {formData.type === 'DATE' && (() => {
          let config = { placeholder: '请选择日期' };
          try { config = { ...config, ...JSON.parse(formData.settings || '{}') }; } catch {}
          const updateDateSettings = (key: string, value: string) => {
            const updated = { ...config, [key]: value };
            setFormData({ ...formData, settings: JSON.stringify(updated) });
          };
          return (
            <div className="mb-6">
              <label className="block text-sm font-medium font-label text-on-surface mb-3">日期题设置</label>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1 font-label">占位文本</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 text-sm"
                    value={config.placeholder}
                    onChange={(e) => updateDateSettings('placeholder', e.target.value)}
                    placeholder="请选择日期"
                  />
                </div>
              </div>
            </div>
          );
        })()}

        {/* 短文本设置 */}
        {formData.type === 'SHORT_TEXT' && (() => {
          let config = { maxLength: 100, placeholder: '请输入' };
          try { config = { ...config, ...JSON.parse(formData.settings || '{}') }; } catch {}
          const updateShortTextSettings = (key: string, value: string | number) => {
            const updated = { ...config, [key]: value };
            setFormData({ ...formData, settings: JSON.stringify(updated) });
          };
          return (
            <div className="mb-6">
              <label className="block text-sm font-medium font-label text-on-surface mb-3">短文本设置</label>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1 font-label">最大字数</label>
                  <input
                    type="number"
                    className="w-32 px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 text-sm"
                    value={config.maxLength}
                    onChange={(e) => updateShortTextSettings('maxLength', parseInt(e.target.value) || 100)}
                    min={1}
                    max={1000}
                  />
                </div>
                <div>
                  <label className="block text-xs text-on-surface-variant mb-1 font-label">占位文本</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200 text-sm"
                    value={config.placeholder}
                    onChange={(e) => updateShortTextSettings('placeholder', e.target.value)}
                    placeholder="请输入"
                  />
                </div>
              </div>
            </div>
          );
        })()}

        {/* 滑块设置 */}
        {formData.type === 'SLIDER' && (() => {
          const sliderError = validateSliderSettings(formData.settings || '');
          const hasContent = !!formData.settings?.trim();
          return (
            <div className="mb-6">
              <label className="block text-sm font-medium font-label text-on-surface mb-2">滑块设置（JSON 格式）</label>
              <textarea
                className={`w-full px-3 py-2 rounded-xl focus:outline-none focus:ring-2 font-mono text-sm ${
                  hasContent && sliderError ? 'border-danger-400 focus:ring-danger-500 border'
                    : hasContent && !sliderError ? 'border-success-400 focus:ring-success-500 border'
                    : 'bg-surface-container-high focus:ring-primary-200'
                }`}
                rows={4}
                value={formData.settings}
                onChange={(e) => setFormData({ ...formData, settings: e.target.value })}
                placeholder='{"min": 0, "max": 100, "step": 1}'
              />
              {hasContent && sliderError && <p className="mt-1 text-xs text-danger-600 font-label">{sliderError}</p>}
              {hasContent && !sliderError && <p className="mt-1 text-xs text-success-600 font-label">JSON 格式正确</p>}
            </div>
          );
        })()}

        {/* 矩阵题设置 */}
        {formData.type === 'MATRIX' && (
          <MatrixEditor
            value={formData.settings || ''}
            onChange={(json) => setFormData({ ...formData, settings: json })}
          />
        )}

        {/* 说明文本设置 */}
        {formData.type === 'DESCRIPTION' && (
          <div className="mb-6">
            <label className="block text-sm font-medium font-label text-on-surface mb-2">说明内容（支持HTML格式）*</label>
            <RichTextEditor
              value={formData.settings || ''}
              onChange={(value) => setFormData({ ...formData, settings: value })}
              placeholder="请输入说明内容，支持富文本格式..."
            />
          </div>
        )}

        {/* 填空题 / 动态填空题设置 */}
        {(formData.type === 'FILL_IN_BLANK' || formData.type === 'DYNAMIC_FILL_IN_BLANK') && (() => {
          let fields: { key: string; label: string; suffix: string; placeholder: string }[] = [];
          let minGroups = 1;
          let maxGroups = 5;
          try {
            const parsed = JSON.parse(formData.settings || '{"fields":[]}');
            fields = parsed.fields || [];
            if (parsed.minGroups !== undefined) minGroups = parsed.minGroups;
            if (parsed.maxGroups !== undefined) maxGroups = parsed.maxGroups;
          } catch {
            fields = [{ key: 'field_1', label: '', suffix: '', placeholder: '请输入' }];
          }

          const updateFields = (newFields: typeof fields) => {
            if (formData.type === 'DYNAMIC_FILL_IN_BLANK') {
              setFormData({ ...formData, settings: JSON.stringify({ fields: newFields, minGroups, maxGroups }, null, 2) });
            } else {
              setFormData({ ...formData, settings: JSON.stringify({ fields: newFields }, null, 2) });
            }
          };

          return (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium font-label text-on-surface">输入框配置 *</label>
                <button
                  type="button"
                  onClick={() => updateFields([...fields, { key: `field_${fields.length + 1}`, label: '', suffix: '', placeholder: '请输入' }])}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  <PlusIcon size={16} />
                  添加输入框
                </button>
              </div>
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div key={field.key} className="shadow-soft rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-surface-container-high rounded-full flex items-center justify-center text-sm font-medium text-on-surface-variant">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1 font-label">字段标识 (key)</label>
                            <input type="text" className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200" value={field.key}
                              onChange={(e) => { const nf = [...fields]; nf[index] = { ...nf[index], key: e.target.value }; updateFields(nf); }} placeholder="如：height" />
                          </div>
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1 font-label">标签 (label)</label>
                            <input type="text" className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200" value={field.label}
                              onChange={(e) => { const nf = [...fields]; nf[index] = { ...nf[index], label: e.target.value }; updateFields(nf); }} placeholder="如：身高" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1 font-label">后缀文字 (suffix)</label>
                            <input type="text" className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200" value={field.suffix}
                              onChange={(e) => { const nf = [...fields]; nf[index] = { ...nf[index], suffix: e.target.value }; updateFields(nf); }} placeholder="如：厘米" />
                          </div>
                          <div>
                            <label className="block text-xs text-on-surface-variant mb-1 font-label">占位符 (placeholder)</label>
                            <input type="text" className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200" value={field.placeholder}
                              onChange={(e) => { const nf = [...fields]; nf[index] = { ...nf[index], placeholder: e.target.value }; updateFields(nf); }} placeholder="请输入" />
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button type="button" onClick={() => { if (index === 0) return; const nf = [...fields]; [nf[index - 1], nf[index]] = [nf[index], nf[index - 1]]; updateFields(nf); }}
                          disabled={index === 0} className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed" title="上移"><ChevronUpIcon size={20} /></button>
                        <button type="button" onClick={() => { if (index === fields.length - 1) return; const nf = [...fields]; [nf[index], nf[index + 1]] = [nf[index + 1], nf[index]]; updateFields(nf); }}
                          disabled={index === fields.length - 1} className="p-1 text-on-surface-variant hover:text-on-surface disabled:opacity-30 disabled:cursor-not-allowed" title="下移"><ChevronDownIcon size={20} /></button>
                        <button type="button" onClick={() => { if (fields.length <= 1) return; updateFields(fields.filter((_, i) => i !== index)); }}
                          disabled={fields.length <= 1} className="p-1 text-danger-600 hover:text-danger-700 disabled:opacity-30 disabled:cursor-not-allowed" title="删除"><TrashIcon size={20} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {formData.type === 'DYNAMIC_FILL_IN_BLANK' && (
                <div className="mt-4 p-4 shadow-soft rounded-2xl bg-surface-container-low">
                  <label className="block text-sm font-medium font-label text-on-surface mb-3">组数限制</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">最少组数</label>
                      <input type="number" min={1} max={maxGroups} className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                        value={minGroups} onChange={(e) => { const val = Math.max(1, parseInt(e.target.value) || 1); setFormData({ ...formData, settings: JSON.stringify({ fields, minGroups: val, maxGroups }, null, 2) }); }} />
                    </div>
                    <div>
                      <label className="block text-xs text-on-surface-variant mb-1 font-label">最多组数</label>
                      <input type="number" min={minGroups} max={50} className="w-full px-3 py-2 text-sm bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
                        value={maxGroups} onChange={(e) => { const val = Math.max(minGroups, parseInt(e.target.value) || 1); setFormData({ ...formData, settings: JSON.stringify({ fields, minGroups, maxGroups: val }, null, 2) }); }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* 操作按钮 */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(`/question-banks/${bankId}`)}
            className="flex-1 px-4 py-2 bg-surface-container-high text-on-surface rounded-xl hover:bg-surface-container-low"
            disabled={loading}
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 gradient-primary text-on-primary shadow-teal rounded-xl hover:opacity-90 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankQuestionEditPage;
